import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, Platform, StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { Coins } from 'components/Icons';
import { useRemScale } from 'lib/remScale';
import { TYPE, FONTS } from 'style/typography';
import { playCoins } from 'firstVisit/coinSound';
import { flightToStore } from 'firstVisit/targets';

const POP_MS = 900;
const DONE_MS = 1900;
const SPREAD = [-30, 0, 30];

/**
 * QuikCoins paid for a Quikbase control in the first visit (owner round 11 D4; prototype CoinBurst): three coins pop up
 * with "+10 QuikCoins" and the coin sound - the same coins and sound as a badge moment - then fly into the Collection
 * Store. Shown only after the server paid. Never blocks the world.
 */
export default function CoinBurst({ amount, onDone }) {
  const { rem } = useRemScale();
  const { height } = useWindowDimensions();
  const pop = useRef(new Animated.Value(0)).current;
  const fly = useRef(new Animated.Value(0)).current;
  const [target, setTarget] = useState({ x: 0, y: 0 });
  const native = Platform.OS !== 'web';

  useEffect(() => {
    playCoins(2);
    Animated.spring(pop, { toValue: 1, friction: 6, tension: 80, useNativeDriver: native }).start();
    const flyTimer = setTimeout(() => setTarget({ ...flightToStore(), flying: true }), POP_MS);
    const doneTimer = setTimeout(() => onDone && onDone(), DONE_MS);
    return () => {
      clearTimeout(flyTimer);
      clearTimeout(doneTimer);
    };
  }, [native, onDone, pop]);

  // The coins sit above the middle (paddingBottom below); the store offset is measured from the middle.
  const lift = height * 0.08;
  useEffect(() => {
    if (!target.flying) return;
    Animated.timing(fly, { toValue: 1, duration: 800, easing: Easing.bezier(0.5, 0, 0.3, 1), useNativeDriver: native }).start();
  }, [fly, native, target]);

  return (
    <View pointerEvents="none" style={styles.overlay} testID="first-visit-coin-burst">
      {SPREAD.map((dx, i) => (
        <Animated.View
          key={dx}
          style={[
            styles.coin,
            {
              width: rem(26),
              height: rem(26),
              borderRadius: rem(13),
              opacity: fly.interpolate({ inputRange: [0, 0.85, 1], outputRange: [1, 1, 0] }),
              transform: [
                { translateX: fly.interpolate({ inputRange: [0, 1], outputRange: [rem(dx), target.x] }) },
                {
                  translateY: Animated.add(
                    pop.interpolate({ inputRange: [0, 1], outputRange: [rem(30 + i * 6), 0] }),
                    fly.interpolate({ inputRange: [0, 1], outputRange: [0, target.y + lift] }),
                  ),
                },
                { scale: fly.interpolate({ inputRange: [0, 1], outputRange: [1, 0.5] }) },
              ],
            },
          ]}
        />
      ))}
      <Animated.View
        style={[
          styles.amount,
          {
            marginTop: rem(64),
            paddingHorizontal: rem(14),
            paddingVertical: rem(6),
            borderRadius: rem(20),
            opacity: Animated.multiply(pop, fly.interpolate({ inputRange: [0, 0.4, 1], outputRange: [1, 0, 0] })),
          },
        ]}>
        <Coins size={rem(18)} color="#F5C542" />
        <Text style={[styles.amountText, { fontSize: rem(TYPE.lg), fontFamily: FONTS.heading, marginLeft: rem(6) }]}>
          {`+${amount} QuikCoins`}
        </Text>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  // Slightly above the middle, like the prototype (42% of the height).
  overlay: { ...StyleSheet.absoluteFillObject, alignItems: 'center', justifyContent: 'center', paddingBottom: '16%', zIndex: 91 },
  coin: { position: 'absolute', backgroundColor: '#F5C542', borderWidth: 2, borderColor: '#B8860B' },
  amount: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(15,16,20,0.85)',
    borderWidth: 1,
    borderColor: 'rgba(252,211,77,0.45)',
  },
  amountText: { color: '#FCD34D', fontWeight: '800' },
});
